import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';
import { handleAsyncThunkError } from 'redux/reduxHelpers';

const BASE = '/crm/dashboard/crm';

// The overview composes ~8 heavy aggregations; give cold loads room beyond the
// default 30s client timeout so a slow-but-successful first load doesn't abort
// (which otherwise surfaces as a blank screen via the global error handling).
const HEAVY_TIMEOUT = 60000;

// Composed overview payload: { job_entry, contract, sales, quotation, waiver, lost, meta }
export const fetchOverview = createAsyncThunk(
  'dashboardCrmOverview/fetchOverview',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(BASE, { params, timeout: HEAVY_TIMEOUT });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch CRM overview'));
    }
  }
);

// Standalone lost report (also embedded in the overview payload under `lost`).
export const fetchOverviewLost = createAsyncThunk(
  'dashboardCrmOverview/fetchLost',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_lost_report`, { params, timeout: HEAVY_TIMEOUT });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch lost report'));
    }
  }
);

export const fetchOverviewFilterOptions = createAsyncThunk(
  'dashboardCrmOverview/fetchFilterOptions',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/filter_options`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch filter options'));
    }
  }
);

// Curated analytical bundle for one tab (lazy-loaded). `params.tab` selects it;
// the payload is stored keyed by tab so switching tabs keeps prior data.
export const fetchTabData = createAsyncThunk(
  'dashboardCrmOverview/fetchTabData',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_tab_data`, { params, timeout: HEAVY_TIMEOUT });
      return { tab: params.tab, data: res.data.data };
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch tab data'));
    }
  }
);

const makeSection = () => ({ data: null, loading: false, error: null });

const initialState = {
  overview:      makeSection(),
  lost:          makeSection(),
  filterOptions: makeSection(),
  tabs:          {}, // keyed by tab: { data, loading, error }
  lastUpdated:   null
};

const thunkMap = [
  ['overview',      fetchOverview],
  ['lost',          fetchOverviewLost],
  ['filterOptions', fetchOverviewFilterOptions]
];

const dashboardCrmOverviewSlice = createSlice({
  name: 'dashboardCrmOverview',
  initialState,
  reducers: {
    resetOverviewDashboard: () => initialState,
    resetError: (state) => {
      Object.keys(state).forEach((k) => {
        if (state[k] && typeof state[k] === 'object' && 'error' in state[k]) {
          state[k].error = null;
        }
      });
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTabData.pending, (state, action) => {
        const tab = action.meta.arg?.tab;
        if (tab) state.tabs[tab] = { ...(state.tabs[tab] || {}), loading: true, error: null };
      })
      .addCase(fetchTabData.fulfilled, (state, action) => {
        const { tab, data } = action.payload;
        state.tabs[tab] = { data, loading: false, error: null };
      })
      .addCase(fetchTabData.rejected, (state, action) => {
        const tab = action.meta.arg?.tab;
        if (tab) state.tabs[tab] = { ...(state.tabs[tab] || {}), loading: false, error: action.payload };
      });

    thunkMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[key].loading = true;
          state[key].error   = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key].data    = action.payload;
          state[key].loading = false;
          state.lastUpdated  = new Date().toISOString();
        })
        .addCase(thunk.rejected, (state, action) => {
          state[key].loading = false;
          state[key].error   = action.payload;
        });
    });
  }
});

export const { resetOverviewDashboard, resetError } = dashboardCrmOverviewSlice.actions;
export default dashboardCrmOverviewSlice.reducer;
